import {Component, EventEmitter, inject, OnInit, OnDestroy, Output, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NZ_DRAWER_DATA, NzDrawerRef} from 'ng-zorro-antd/drawer';
import {ProjectUtilsService} from '../../services/project-utils.service';
import {EventType, Flow, ToastConfig} from '@app/shared';
import {GlobalValuesService} from '../../../../core/services/global-values.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {cloneFormArray, deepClone, DrawerEscapeBlockerService, FlowEventService} from '@app/core';
import {NzNotificationService} from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-edit-flow-drawer',
  standalone: false,

  templateUrl: './edit-flow-drawer.component.html',
  styleUrl: './edit-flow-drawer.component.less',
})
export class EditFlowDrawerComponent implements OnInit {
  nzData: { events: FormArray; originalFlow: Flow } = inject(NZ_DRAWER_DATA);

  @Output() newEvents = new EventEmitter<EventType[]>();

  eventsToEdit: FormArray | undefined;

  step = 2;

  selectedEvent: EventType | undefined;
  flowsList: Flow[] = [];

  editingFlow: boolean = false;

  eventForm: FormGroup | undefined;
  activatorForm: FormGroup | undefined;

  selectedFlow: Flow | undefined;

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Flujo editado',
    showUndo: false,
  };

  cloneFormArray = cloneFormArray;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    public ps: ProjectUtilsService,
    public gs: GlobalValuesService,
    private flowEventService: FlowEventService,
    private fb: FormBuilder,
    private notificationService: NzNotificationService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    this.gs.flows$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.flowsList = deepClone(value)));
  }

  ngOnInit() {
    this.eventsToEdit = this.cloneFormArray(this.nzData.events);
  }

  close() {
    this.editingFlow = false;
    this.drawerRef?.close();
  }

  cancelAddOrEditEvent(): void {
    this.selectedEvent = undefined;
    this.step = 2;
  }

  openAddOrEditEvent(eventId: number | null): void {
    this.selectedEvent = eventId
      ? this.eventsToEdit!.value.find((e: EventType) => e.id === eventId)
      : undefined;
    this.step = 3;
  }

  setEvents(flow: Flow) {
    this.gs.setEventFlow([flow]);
    this.flowEventService.setEvents(flow, this.eventsToEdit!, this.fb);
  }

  async editFlow(): Promise<void> {
    this.editingFlow = true;
    try {
      const updatedFlow = await this.flowEventService.editFlow(
        this.nzData.originalFlow,
        this.eventsToEdit!,
      );

      // Actualizar el selectedService con el flujo actualizado
      if (this.ps.selectedService) {
        this.ps.selectedService.flow = updatedFlow;
      }

      this.newEvents.emit(updatedFlow.events);
      this.toastConfig = {
        icon: 'check_circle',
        iconColor: '#6CDA75',
        content: 'Flujo editado',
        showUndo: false,
      };
      this.showNotification();
    } catch (e: any) {
      console.error(e);
      this.toastConfig.content = 'Error al tratar de editar el flujo!';
      this.toastConfig.msg = e.error.message;
      this.toastConfig.icon = 'dangerous';
      this.toastConfig.iconColor = '#CB2A2F';
      this.showNotification();
    }
  }

  showNotification(): void {
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' },
    );

    this.close();
  }

  isFormsValids(): boolean {
    if (this.eventForm && this.activatorForm) {
      return this.flowEventService.isFormsValids(
        this.eventForm,
        this.activatorForm,
      );
    } else return false;
  }

  confirmAddOrEditEvent(): void {
    if (this.selectedEvent) {
      const index = this.eventsToEdit!.value.findIndex(
        (control: any) => control.id === this.selectedEvent?.id,
      );

      // Actualizar el FormControl en el FormArray
      this.eventsToEdit!.at(index).patchValue({
        ...this.selectedEvent,
        ...this.eventForm?.value,
      });
    } else {
      const currentLength = this.eventsToEdit!.length;
      const insertIndex = Math.max(0, currentLength - 1);

      // 1. Configurar sequence del NUEVO evento (penúltimo)
      this.eventForm!.get('sequence')?.setValue(currentLength); // Usamos currentLength, no +1

      // 2. Insertar en penúltima posición
      this.eventsToEdit!.insert(insertIndex, this.eventForm!);

      // 3. Actualizar el ULTIMO elemento (que ahora está desplazado)
      if (currentLength > 0) {
        const lastIndex = this.eventsToEdit!.length - 1;
        const lastControl = this.eventsToEdit!.at(lastIndex);
        lastControl.get('sequence')?.setValue(currentLength + 1);
      }
    }
    this.selectedEvent = undefined;
    this.step = 2;
  }

  ngOnDestroy(): void {
    this.drawerEscapeBlocker.unblock();
  }
}
